import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Save, RotateCcw, Plus, Trash2, ExternalLink, Inbox, FileEdit, Upload, GripVertical, ImageIcon, Images, Copy, X, LayoutGrid } from "lucide-react";
import { api, formatApiErrorDetail, mediaUrl, LOGO_URL, P_LOGO_URL } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useContent } from "../../context/ContentContext";

const SECTION_LABELS = {
  brand: "Marca",
  seo: "SEO",
  nav: "Navegação",
  hero: "Hero",
  marquee: "Marquee",
  services: "Serviços",
  about: "Sobre",
  process: "Processo",
  portfolio: "Casos de Sucesso",
  contact: "Contacto",
  footer: "Rodapé",
  legal: "Legal / Cookies",
};

function titleize(key) {
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

const inputCls =
  "w-full bg-peca-black border border-peca-line focus:border-peca-yellow outline-none px-3 py-2 text-peca-cream text-sm transition-colors duration-200";

function Str({ label, value, onChange, testid }) {
  const long = (value || "").length > 55 || (value || "").includes("\n");
  return (
    <div>
      {label && <label className="label-mono text-peca-grey block mb-2">{label}</label>}
      {long ? (
        <textarea data-testid={testid} rows={3} className={`${inputCls} resize-y`} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input data-testid={testid} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function MediaModal({ open, onClose, onSelect }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/media").then(({ data }) => setMedia(data)).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="media-modal">
      <div className="bg-peca-surface border border-peca-line w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-peca-line">
          <span className="font-display font-bold uppercase text-peca-cream tracking-tight">Escolher da galeria</span>
          <button onClick={onClose} className="text-peca-grey hover:text-peca-yellow" data-testid="media-modal-close"><X size={20} /></button>
        </div>
        <div className="p-4 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-peca-yellow" /></div>
          ) : media.length === 0 ? (
            <p className="text-peca-grey text-sm py-6 text-center">Ainda não há imagens carregadas.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {media.map((m) => (
                <button
                  key={m.id}
                  onClick={() => { onSelect(m.url); onClose(); }}
                  data-testid={`media-pick-${m.id}`}
                  className="group aspect-square border border-peca-line overflow-hidden hover:border-peca-yellow transition-colors"
                >
                  <img src={mediaUrl(m.url)} alt={m.original_filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Imagem carregada.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha no upload.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      {label && <label className="label-mono text-peca-grey block mb-2">{label}</label>}
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 border border-peca-line bg-peca-black shrink-0 overflow-hidden flex items-center justify-center">
          {value ? (
            <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={18} className="text-peca-grey" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <input data-testid={testid} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} placeholder="URL da imagem, carrega ou escolhe da galeria" />
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testid}-file`} />
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          data-testid={`${testid}-gallery`}
          title="Escolher da galeria"
          className="inline-flex items-center gap-2 label-mono border border-peca-line text-peca-grey hover:text-peca-yellow hover:border-peca-yellow px-3 py-3 transition-colors shrink-0"
        >
          <Images size={14} />
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testid}-upload`}
          title="Carregar imagem"
          className="inline-flex items-center gap-2 label-mono bg-peca-yellow text-peca-black px-3 py-3 hover:bg-peca-cream transition-colors disabled:opacity-60 shrink-0"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        </button>
      </div>
      <MediaModal open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={onChange} />
    </div>
  );
}

function MediaGallery() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/media");
      setMedia(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Apagar esta imagem? Deixará de estar disponível no site.")) return;
    try {
      await api.delete(`/media/${id}`);
      setMedia((m) => m.filter((x) => x.id !== id));
      toast.success("Imagem apagada.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(mediaUrl(url));
      toast.success("URL copiado.");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-peca-yellow" /></div>;
  if (media.length === 0) return <p className="text-peca-grey">Ainda não há imagens carregadas. Carrega imagens a partir dos campos de imagem das secções.</p>;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {media.map((m) => (
        <div key={m.id} data-testid={`media-item-${m.id}`} className="border border-peca-line bg-peca-surface group">
          <div className="aspect-square overflow-hidden bg-peca-black">
            <img src={mediaUrl(m.url)} alt={m.original_filename} className="w-full h-full object-cover" />
          </div>
          <div className="flex items-center justify-between p-2">
            <span className="label-mono text-peca-grey truncate max-w-[70%]" title={m.original_filename}>{m.original_filename || "imagem"}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => copy(m.url)} data-testid={`media-copy-${m.id}`} title="Copiar URL" className="text-peca-grey hover:text-peca-yellow transition-colors"><Copy size={15} /></button>
              <button onClick={() => remove(m.id)} data-testid={`media-delete-${m.id}`} title="Apagar" className="text-peca-grey hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FieldFor({ k, v, onChange, testid }) {
  const isImage = /image|photo|foto|logo/i.test(k) && typeof v === "string";
  if (isImage) return <ImageField label={titleize(k)} value={v} onChange={onChange} testid={testid} />;
  return <Str label={titleize(k)} value={typeof v === "string" ? v : JSON.stringify(v)} onChange={onChange} testid={testid} />;
}

/* Object made of primitive string values (bounded, non-recursive) */
function ObjFields({ obj, onChange, prefix }) {
  return (
    <div className="flex flex-col gap-3">
      {Object.entries(obj).map(([k, v]) => (
        <FieldFor key={k} k={k} v={v} onChange={(nv) => onChange({ ...obj, [k]: nv })} testid={`field-${prefix}-${k}`} />
      ))}
    </div>
  );
}

function blankLike(item) {
  if (typeof item === "string") return "";
  const out = {};
  for (const k of Object.keys(item)) out[k] = "";
  return out;
}

function ArrayEditor({ label, items, onChange, prefix, template }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const addItem = () => {
    const base = items.length ? items[items.length - 1] : (template != null ? template : "");
    onChange([...items, blankLike(base)]);
  };
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const setItem = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  const move = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div>
      {label && <label className="label-mono text-peca-yellow block mb-3">{label}</label>}
      <div className="flex flex-col gap-3">
        {items.map((item, i) => (
          <div
            key={i}
            data-testid={`item-${prefix}-${i}`}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
            onDrop={(e) => { e.preventDefault(); move(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
            className={`border bg-peca-black/40 p-3 transition-colors ${overIdx === i && dragIdx !== null ? "border-peca-yellow" : "border-peca-line"}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                  data-testid={`drag-${prefix}-${i}`}
                  className="cursor-grab active:cursor-grabbing text-peca-grey hover:text-peca-yellow"
                  title="Arrastar para reordenar"
                >
                  <GripVertical size={16} />
                </span>
                <span className="label-mono text-peca-grey">Item {i + 1}</span>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} data-testid={`up-${prefix}-${i}`} className="text-peca-grey hover:text-peca-yellow disabled:opacity-30 transition-colors text-xs font-mono-p">↑</button>
                <button type="button" onClick={() => move(i, i + 1)} disabled={i === items.length - 1} data-testid={`down-${prefix}-${i}`} className="text-peca-grey hover:text-peca-yellow disabled:opacity-30 transition-colors text-xs font-mono-p">↓</button>
                <button type="button" onClick={() => removeItem(i)} data-testid={`remove-${prefix}-${i}`} className="text-peca-grey hover:text-red-400 transition-colors" aria-label="Remover">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            {typeof item === "string" ? (
              <Str value={item} onChange={(v) => setItem(i, v)} testid={`field-${prefix}-${i}`} />
            ) : (
              <ObjFields obj={item} onChange={(v) => setItem(i, v)} prefix={`${prefix}-${i}`} />
            )}
          </div>
        ))}
        <button type="button" onClick={addItem} data-testid={`add-${prefix}`} className="inline-flex items-center gap-2 self-start label-mono text-peca-black bg-peca-yellow px-3 py-2 hover:bg-peca-cream transition-colors">
          <Plus size={14} /> Adicionar
        </button>
      </div>
    </div>
  );
}

/* One section = object whose fields are string | array | object-of-strings */
function SectionEditor({ section, data, onChange }) {
  return (
    <div className="flex flex-col gap-6">
      {Object.entries(data).map(([key, val]) => {
        const setField = (v) => onChange({ ...data, [key]: v });
        if (typeof val === "string") {
          return <FieldFor key={key} k={key} v={val} onChange={setField} testid={`field-${section}-${key}`} />;
        }
        if (Array.isArray(val)) {
          return <ArrayEditor key={key} label={titleize(key)} items={val} onChange={setField} prefix={`${section}-${key}`} />;
        }
        if (val && typeof val === "object") {
          return (
            <div key={key} className="border border-peca-line p-4">
              <label className="label-mono text-peca-yellow block mb-3">{titleize(key)}</label>
              <ObjFields obj={val} onChange={setField} prefix={`${section}-${key}`} />
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

const CARD_TEMPLATE = { title: "Cartão", description: "Descrição", image: "", link: "" };
const PROJECT_TEMPLATE = { title: "Projeto", client: "Cliente", category: "Categoria", image: "", link: "" };
const TYPE_LABELS = { text: "Texto", cards: "Cartões / Imagens", portfolio: "Portfólio", form: "Formulário" };

let SEQ = 0;
function newTypedSection(type = "text") {
  SEQ += 1;
  const id = `sec-${Date.now().toString(36)}${SEQ}`;
  const base = { id, kind: "custom", type, visible: true, eyebrow: "", title: "Nova secção", intro: "" };
  if (type === "text") base.body = "Escreve aqui o teu texto. Usa uma linha em branco para separar parágrafos.";
  if (type === "cards") base.cards = [{ ...CARD_TEMPLATE }];
  if (type === "portfolio") base.projects = [{ ...PROJECT_TEMPLATE }];
  if (type === "form") { base.title = "Fala connosco"; base.successMessage = "Mensagem enviada!"; }
  return base;
}

function TypeSelect({ value, onChange, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={inputCls}>
      {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function TypedSectionEditor({ section, onChange, prefix }) {
  const set = (patch) => onChange({ ...section, ...patch });
  const changeType = (type) => {
    const next = { ...section, type };
    if (type === "cards" && !next.cards) next.cards = [{ ...CARD_TEMPLATE }];
    if (type === "portfolio" && !next.projects) next.projects = [{ ...PROJECT_TEMPLATE }];
    if (type === "text" && next.body == null) next.body = "";
    onChange(next);
  };
  return (
    <div className="flex flex-col gap-4">
      <div>
        <label className="label-mono text-peca-grey block mb-2">Tipo de secção</label>
        <TypeSelect value={section.type || "text"} onChange={changeType} testid={`${prefix}-type`} />
      </div>
      <Str label="Eyebrow" value={section.eyebrow || ""} onChange={(v) => set({ eyebrow: v })} testid={`${prefix}-eyebrow`} />
      <Str label="Título" value={section.title || ""} onChange={(v) => set({ title: v })} testid={`${prefix}-title`} />
      <Str label="Intro" value={section.intro || ""} onChange={(v) => set({ intro: v })} testid={`${prefix}-intro`} />
      {section.type === "text" && (
        <Str label="Texto" value={section.body || ""} onChange={(v) => set({ body: v })} testid={`${prefix}-body`} />
      )}
      {section.type === "cards" && (
        <ArrayEditor label="Cartões" items={section.cards || []} template={CARD_TEMPLATE} onChange={(v) => set({ cards: v })} prefix={`${prefix}-cards`} />
      )}
      {section.type === "portfolio" && (
        <ArrayEditor label="Projetos" items={section.projects || []} template={PROJECT_TEMPLATE} onChange={(v) => set({ projects: v })} prefix={`${prefix}-projects`} />
      )}
      {section.type === "form" && (
        <Str label="Mensagem de sucesso" value={section.successMessage || ""} onChange={(v) => set({ successMessage: v })} testid={`${prefix}-success`} />
      )}
    </div>
  );
}

/* Manages an ordered array of typed sections (used by pages) */
function SectionListEditor({ items, onChange, prefix }) {
  const [type, setType] = useState("text");
  const add = () => onChange([...(items || []), newTypedSection(type)]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const setItem = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };
  return (
    <div className="flex flex-col gap-4">
      {(items || []).map((s, i) => (
        <div key={s.id || i} data-testid={`${prefix}-item-${i}`} className="border border-peca-line p-4">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-peca-line">
            <span className="label-mono text-peca-grey">{TYPE_LABELS[s.type] || "Secção"} #{i + 1}</span>
            <div className="flex items-center gap-3">
              <span className="label-mono text-peca-grey">Visível</span>
              <Toggle on={s.visible !== false} onChange={(v) => setItem(i, { ...s, visible: v })} testid={`${prefix}-visible-${i}`} />
              <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} className="text-peca-grey hover:text-peca-yellow disabled:opacity-30 font-mono-p text-xs">↑</button>
              <button type="button" onClick={() => move(i, i + 1)} disabled={i === items.length - 1} className="text-peca-grey hover:text-peca-yellow disabled:opacity-30 font-mono-p text-xs">↓</button>
              <button type="button" onClick={() => remove(i)} data-testid={`${prefix}-delete-${i}`} className="text-peca-grey hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
          <TypedSectionEditor section={s} onChange={(v) => setItem(i, v)} prefix={`${prefix}-${i}`} />
        </div>
      ))}
      <div className="flex items-center gap-2">
        <div className="w-52"><TypeSelect value={type} onChange={setType} testid={`${prefix}-newtype`} /></div>
        <button type="button" onClick={add} data-testid={`${prefix}-add`} className="inline-flex items-center gap-2 label-mono bg-peca-yellow text-peca-black px-3 py-2 hover:bg-peca-cream transition-colors">
          <Plus size={14} /> Adicionar secção
        </button>
      </div>
    </div>
  );
}

const BUILTIN_LABELS = {
  marquee: "Marquee",
  services: "Serviços",
  about: "Sobre",
  process: "Processo",
  portfolio: "Casos de Sucesso",
  contact: "Contacto",
};

function Toggle({ on, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      data-testid={testid}
      className={`w-11 h-6 flex items-center px-0.5 transition-colors ${on ? "bg-peca-yellow justify-end" : "bg-peca-line justify-start"}`}
      aria-pressed={on}
    >
      <span className={`w-5 h-5 block ${on ? "bg-peca-black" : "bg-peca-grey"}`} />
    </button>
  );
}

/* Home page: reorder built-in + custom blocks, toggle built-in visibility */
function HomeBlocksEditor({ draft, setDraft }) {
  const visibility = draft.visibility || {};
  const blocks = draft.homeBlocks || [];
  const [type, setType] = useState("text");

  const setBlocks = (arr) => setDraft({ ...draft, homeBlocks: arr });
  const setVisibility = (k, v) => setDraft({ ...draft, visibility: { ...visibility, [k]: v } });
  const move = (from, to) => {
    if (to < 0 || to >= blocks.length) return;
    const next = [...blocks];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setBlocks(next);
  };
  const setBlock = (i, v) => setBlocks(blocks.map((b, idx) => (idx === i ? v : b)));
  const remove = (i) => setBlocks(blocks.filter((_, idx) => idx !== i));
  const addCustom = () => setBlocks([...blocks, newTypedSection(type)]);

  return (
    <div className="flex flex-col gap-6">
      <p className="text-peca-grey text-sm">Arrasta com as setas para reordenar as secções da página inicial, liga/desliga a visibilidade, ou adiciona secções personalizadas (texto, imagens, portfólio ou formulário).</p>
      {blocks.map((b, i) => {
        const isBuiltin = b.kind === "builtin";
        return (
          <div key={b.key || b.id || i} data-testid={`home-block-${i}`} className="border border-peca-line p-4">
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-peca-line">
              <span className="font-display font-bold uppercase tracking-tight text-sm text-peca-cream">
                {isBuiltin ? BUILTIN_LABELS[b.key] || b.key : `${TYPE_LABELS[b.type] || "Personalizada"} (personalizada)`}
              </span>
              <div className="flex items-center gap-3">
                <span className="label-mono text-peca-grey">Visível</span>
                <Toggle
                  on={isBuiltin ? visibility[b.key] !== false : b.visible !== false}
                  onChange={(v) => (isBuiltin ? setVisibility(b.key, v) : setBlock(i, { ...b, visible: v }))}
                  testid={isBuiltin ? `vis-toggle-${b.key}` : `home-custom-visible-${i}`}
                />
                <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} data-testid={`home-up-${i}`} className="text-peca-grey hover:text-peca-yellow disabled:opacity-30 font-mono-p text-xs">↑</button>
                <button type="button" onClick={() => move(i, i + 1)} disabled={i === blocks.length - 1} data-testid={`home-down-${i}`} className="text-peca-grey hover:text-peca-yellow disabled:opacity-30 font-mono-p text-xs">↓</button>
                {!isBuiltin && <button type="button" onClick={() => remove(i)} data-testid={`home-delete-${i}`} className="text-peca-grey hover:text-red-400 transition-colors"><Trash2 size={16} /></button>}
              </div>
            </div>
            {isBuiltin ? (
              <p className="label-mono text-peca-grey">Edita o conteúdo desta secção no separador «{BUILTIN_LABELS[b.key] || b.key}».</p>
            ) : (
              <TypedSectionEditor section={b} onChange={(v) => setBlock(i, v)} prefix={`home-${i}`} />
            )}
          </div>
        );
      })}
      <div className="flex items-center gap-2">
        <div className="w-52"><TypeSelect value={type} onChange={setType} testid="home-newtype" /></div>
        <button type="button" onClick={addCustom} data-testid="add-custom-section" className="inline-flex items-center gap-2 label-mono bg-peca-yellow text-peca-black px-3 py-2 hover:bg-peca-cream transition-colors">
          <Plus size={14} /> Adicionar secção
        </button>
      </div>
    </div>
  );
}

function slugify(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

/* Pages manager: create/reorder/delete pages, edit each page's typed sections */
function PagesEditor({ draft, setDraft }) {
  const pages = draft.pages || [];
  const [openIdx, setOpenIdx] = useState(null);

  const setPages = (arr) => setDraft({ ...draft, pages: arr });
  const setPage = (i, v) => setPages(pages.map((p, idx) => (idx === i ? v : p)));
  const move = (from, to) => {
    if (to < 0 || to >= pages.length) return;
    const next = [...pages];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setPages(next);
  };
  const addPage = () => {
    const n = pages.length + 1;
    const title = `Nova Página ${n}`;
    const slug = slugify(title);
    const page = { id: `page-${Date.now().toString(36)}`, title, slug, showInNav: true, sections: [newTypedSection("text")] };
    setDraft({ ...draft, pages: [...pages, page] });
    setOpenIdx(pages.length);
  };
  const removePage = (i) => {
    setDraft({ ...draft, pages: pages.filter((_, idx) => idx !== i) });
    setOpenIdx(null);
  };
  const setTitle = (i, v) => {
    const p = pages[i];
    // Keep the slug auto-derived from the title until the admin edits the slug manually.
    const autoSlug = !p.slug || p.slug === slugify(p.title);
    setPage(i, { ...p, title: v, slug: autoSlug ? slugify(v) : p.slug });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <p className="text-peca-grey text-sm max-w-xl">Cria páginas próprias (ex.: «Sobre Nós», «Serviços»). Cada página aparece em <span className="text-peca-cream">/slug</span> e, com «No menu» ligado, surge automaticamente no menu do topo.</p>
        <button type="button" onClick={addPage} data-testid="add-page" className="inline-flex items-center gap-2 label-mono bg-peca-yellow text-peca-black px-3 py-2 hover:bg-peca-cream transition-colors shrink-0">
          <Plus size={14} /> Nova página
        </button>
      </div>
      {pages.length === 0 && <p className="text-peca-grey text-sm">Ainda não há páginas personalizadas.</p>}
      {pages.map((p, i) => (
        <div key={p.id || i} data-testid={`page-editor-${i}`} className="border border-peca-line">
          <div className="flex items-center justify-between p-4 bg-peca-black/40">
            <button type="button" onClick={() => setOpenIdx(openIdx === i ? null : i)} data-testid={`page-toggle-${i}`} className="flex items-center gap-3 text-left">
              <span className="font-mono-p text-peca-yellow text-xs">{openIdx === i ? "▾" : "▸"}</span>
              <span className="font-display font-bold uppercase tracking-tight text-peca-cream">{p.title}</span>
              <span className="label-mono text-peca-grey">/{p.slug}</span>
            </button>
            <div className="flex items-center gap-3">
              <span className="label-mono text-peca-grey">No menu</span>
              <Toggle on={p.showInNav !== false} onChange={(v) => setPage(i, { ...p, showInNav: v })} testid={`page-nav-${i}`} />
              <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} className="text-peca-grey hover:text-peca-yellow disabled:opacity-30 font-mono-p text-xs">↑</button>
              <button type="button" onClick={() => move(i, i + 1)} disabled={i === pages.length - 1} className="text-peca-grey hover:text-peca-yellow disabled:opacity-30 font-mono-p text-xs">↓</button>
              <button type="button" onClick={() => removePage(i)} data-testid={`delete-page-${i}`} className="text-peca-grey hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
          {openIdx === i && (
            <div className="p-4 flex flex-col gap-4 border-t border-peca-line">
              <Str label="Título" value={p.title} onChange={(v) => setTitle(i, v)} testid={`page-${i}-title`} />
              <Str label="Slug (URL)" value={p.slug} onChange={(v) => setPage(i, { ...p, slug: slugify(v) })} testid={`page-${i}-slug`} />
              <div>
                <label className="label-mono text-peca-yellow block mb-3">Secções da página</label>
                <SectionListEditor items={p.sections || []} onChange={(v) => setPage(i, { ...p, sections: v })} prefix={`page-${i}-sections`} />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { refresh } = useContent();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null); // full multi-locale doc
  const [activeLocale, setActiveLocale] = useState("pt");
  const [active, setActive] = useState("hero");
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  useEffect(() => {
    if (user === false) navigate("/portal");
  }, [user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/content");
        setDraft(data);
        setActiveLocale(data?.defaultLocale || (data?.locales || ["pt"])[0] || "pt");
      } catch {
        toast.error("Falha ao carregar conteúdo.");
      }
    })();
  }, []);

  const loadSubmissions = async () => {
    try {
      const { data } = await api.get("/contact/submissions");
      setSubmissions(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (active === "__submissions") loadSubmissions();
  }, [active]);

  const locales = draft?.locales || ["pt"];
  const localeData = useMemo(() => (draft ? draft[activeLocale] || {} : {}), [draft, activeLocale]);
  const setLocaleData = (next) => setDraft({ ...draft, [activeLocale]: next });
  const sections = useMemo(() => Object.keys(localeData).filter((k) => !["visibility", "customSections", "homeBlocks", "pages"].includes(k)), [localeData]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", { content: draft });
      await refresh();
      toast.success("Conteúdo guardado e publicado.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao guardar.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Repor o conteúdo original? As alterações serão perdidas.")) return;
    try {
      const { data } = await api.post("/content/reset");
      setDraft(data.content);
      await refresh();
      toast.success("Conteúdo reposto.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/portal");
  };

  if (!draft) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-peca-black">
        <Loader2 className="animate-spin text-peca-yellow" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-peca-black flex flex-col md:flex-row">
      <aside className="md:w-64 border-b md:border-b-0 md:border-r border-peca-line bg-peca-surface md:min-h-screen flex flex-col">
        <div className="p-6 border-b border-peca-line">
          <div className="flex items-center gap-2">
            <img src={P_LOGO_URL} alt="Peça" className="h-7 w-auto" />
            <span className="font-display font-extrabold text-peca-cream">CMS</span>
          </div>
          <p className="label-mono text-peca-grey mt-2 truncate">{user?.email}</p>
        </div>
        <nav className="flex-1 p-3 flex flex-col gap-1 overflow-y-auto">
          {sections.map((s) => (
            <button key={s} onClick={() => setActive(s)} data-testid={`tab-${s}`} className={`text-left px-4 py-3 label-mono transition-colors ${active === s ? "bg-peca-yellow text-peca-black" : "text-peca-grey hover:text-peca-cream"}`}>
              {SECTION_LABELS[s] || titleize(s)}
            </button>
          ))}
          <button onClick={() => setActive("__sections")} data-testid="tab-sections" className={`text-left px-4 py-3 label-mono transition-colors inline-flex items-center gap-2 mt-3 border-t border-peca-line ${active === "__sections" ? "bg-peca-yellow text-peca-black" : "text-peca-grey hover:text-peca-cream"}`}>
            <LayoutGrid size={14} /> Página inicial
          </button>
          <button onClick={() => setActive("__pages")} data-testid="tab-pages" className={`text-left px-4 py-3 label-mono transition-colors inline-flex items-center gap-2 ${active === "__pages" ? "bg-peca-yellow text-peca-black" : "text-peca-grey hover:text-peca-cream"}`}>
            <FileEdit size={14} /> Páginas
          </button>
          <button onClick={() => setActive("__media")} data-testid="tab-media" className={`text-left px-4 py-3 label-mono transition-colors inline-flex items-center gap-2 ${active === "__media" ? "bg-peca-yellow text-peca-black" : "text-peca-grey hover:text-peca-cream"}`}>
            <Images size={14} /> Média
          </button>
          <button onClick={() => setActive("__submissions")} data-testid="tab-submissions" className={`text-left px-4 py-3 label-mono transition-colors inline-flex items-center gap-2 ${active === "__submissions" ? "bg-peca-yellow text-peca-black" : "text-peca-grey hover:text-peca-cream"}`}>
            <Inbox size={14} /> Mensagens
          </button>
        </nav>
        <div className="p-3 border-t border-peca-line flex flex-col gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 label-mono text-peca-grey hover:text-peca-yellow px-4 py-2 transition-colors">
            <ExternalLink size={14} /> Ver site
          </a>
          <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 label-mono text-peca-grey hover:text-red-400 px-4 py-2 transition-colors">
            <LogOut size={14} /> Sair
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="sticky top-0 z-10 bg-peca-black/90 backdrop-blur border-b border-peca-line px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileEdit size={18} className="text-peca-yellow" />
            <h1 className="font-display font-bold uppercase text-peca-cream tracking-tight">
              {active === "__submissions" ? "Mensagens recebidas" : active === "__media" ? "Biblioteca de média" : active === "__sections" ? "Página inicial" : active === "__pages" ? "Páginas" : SECTION_LABELS[active] || titleize(active)}
            </h1>
          </div>
          {active !== "__submissions" && active !== "__media" && (
            <div className="flex items-center gap-2">
              {locales.length > 1 && (
                <div className="flex items-center border border-peca-line mr-2" data-testid="admin-locale-switch">
                  {locales.map((l) => (
                    <button
                      key={l}
                      onClick={() => setActiveLocale(l)}
                      data-testid={`admin-lang-${l}`}
                      className={`label-mono px-3 py-2 transition-colors ${activeLocale === l ? "bg-peca-yellow text-peca-black" : "text-peca-grey hover:text-peca-cream"}`}
                    >
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
              <button onClick={reset} data-testid="admin-reset" className="inline-flex items-center gap-2 label-mono border border-peca-line text-peca-grey hover:text-peca-cream px-4 py-2 transition-colors">
                <RotateCcw size={14} /> Repor
              </button>
              <button onClick={save} disabled={saving} data-testid="admin-save" className="inline-flex items-center gap-2 label-mono bg-peca-yellow text-peca-black px-5 py-2 hover:bg-peca-cream transition-colors disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
              </button>
            </div>
          )}
        </div>

        <div className={`p-6 ${active === "__media" || active === "__sections" || active === "__pages" ? "max-w-6xl" : "max-w-3xl"}`}>
          {active === "__media" ? (
            <MediaGallery />
          ) : active === "__sections" ? (
            <HomeBlocksEditor draft={localeData} setDraft={setLocaleData} />
          ) : active === "__pages" ? (
            <PagesEditor draft={localeData} setDraft={setLocaleData} />
          ) : active === "__submissions" ? (
            <div className="flex flex-col gap-4">
              {submissions.length === 0 && <p className="text-peca-grey">Ainda não há mensagens.</p>}
              {submissions.map((m, i) => (
                <div key={i} data-testid={`submission-${i}`} className="border border-peca-line bg-peca-surface p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-display font-bold text-peca-cream">{m.name}</span>
                    <span className="label-mono text-peca-grey">{new Date(m.created_at).toLocaleString("pt-PT")}</span>
                  </div>
                  <a href={`mailto:${m.email}`} className="label-mono text-peca-yellow">{m.email}</a>
                  <p className="text-peca-grey text-sm mt-1 mb-3">Assunto: {m.subject}</p>
                  <p className="text-peca-cream text-sm whitespace-pre-wrap leading-relaxed">{m.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <SectionEditor section={active} data={localeData[active]} onChange={(v) => setLocaleData({ ...localeData, [active]: v })} />
          )}
        </div>
      </main>
    </div>
  );
}
